import { Content } from "@carbon/react";
import { useEffect, useLayoutEffect, useState } from "react";
import { useSelector, useDispatch } from 'react-redux'

import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import './styles/App.scss';
import RouteGuard from "./guards/RouteGuard.js";
import FooterLayout from "./layouts/Footer/index.js"
import HeaderLayout from "./layouts/Header/index.js"
import NotificationsLayout from "./layouts/NotificationsLayout/index.js";
import Dashboard from "./pages/Dashboard/index.js";

import Login from "./pages/Login/index.js";
import Notifications from "./pages/Notifications/index.js";
import ProfileLayout from "./pages/Profile/index.js";
import { selectUser } from "./features/user/userSlice.js";
import {
    ensureAccessTokenThunk,
    initializeAuthThunk,
} from "./features/user/userThunks.js";
import { brandingRequestThunk, instanceRequestThunk } from "./features/branding/brandingThunks.js";
import {
    resetBranding,
    selectBrandingConfig,
} from "./features/branding/brandingSlice.js";
import {
    applyBrandingToDocument,
    clearBrandingFromDocument,
} from "./features/branding/brandingUtils.js";

function App() {
    const dispatch = useDispatch()
    const userState = useSelector(selectUser)
    const brandingConfig = useSelector(selectBrandingConfig)
    const navigate = useNavigate()
    const location = useLocation()
    const [validatedPath, setValidatedPath] = useState(null)
    const [instanceStatus, setInstanceStatus] = useState('loading')

    useEffect(() => {
        dispatch(instanceRequestThunk()).unwrap().then(() => {
            setInstanceStatus('ready')
            dispatch(initializeAuthThunk())
        }).catch(() => setInstanceStatus('error'))
    }, [dispatch])

    useLayoutEffect(() => {
        applyBrandingToDocument(brandingConfig)
    }, [brandingConfig])

    useEffect(() => {
        return () => clearBrandingFromDocument()
    }, [])

    useEffect(() => {
        if (!userState.authInitialized) return undefined

        if (location.pathname === "/login") {
            setValidatedPath("/login")
            return undefined
        }

        let active = true

        dispatch(
            ensureAccessTokenThunk({
                allowRefresh: true,
                trigger: "navigation",
            })
        )
            .unwrap()
            .then(() => dispatch(brandingRequestThunk()).unwrap().catch(() => null))
            .then(() => {
                if (active) setValidatedPath(location.pathname)
            })
            .catch(() => {
                dispatch(resetBranding())
                if (active) navigate('/login', { replace: true })
            })

        return () => {
            active = false
        }
    }, [
        dispatch,
        location.pathname,
        navigate,
        userState.authInitialized,
    ])

    const authChecking =
        !userState.authInitialized ||
        (location.pathname !== "/login" && validatedPath !== location.pathname)

    if (instanceStatus === 'loading') return null;
    if (instanceStatus === 'error') return <div role="alert">No se pudo cargar la configuración de la instancia. <button onClick={() => window.location.reload()}>Reintentar</button></div>;

    return (
        <div className="main-layout">
            <HeaderLayout></HeaderLayout>
            <NotificationsLayout></NotificationsLayout>
            <Content as="main" id="main-content" tabIndex={-1} className="main-container">
                <Routes>
                    <Route exact path="/login" element={<Login />} />
                    <Route path='/*' element={
                        <RouteGuard
                            userState={userState}
                            authChecking={authChecking}
                        >
                            <Routes>
                                <Route path="/" element={<Navigate to="/dashboard" />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/telemetry" element={<Dashboard />} />
                                <Route path="/profile"
                                    element={<ProfileLayout />} />
                                <Route exact path="/notifications"
                                    element={<Notifications />} />
                                <Route exact path="/*"
                                    element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </RouteGuard>

                    }>
                    </Route>
                </Routes>
            </Content>
            <FooterLayout />
        </div>
    );
}

export default App;
